import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CurrencyPipe } from '@angular/common';
import { ColDef, GridOptions } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import { AgChartsModule } from 'ag-charts-angular';
import { AgChartOptions } from 'ag-charts-community';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { take } from 'rxjs';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
import { AccountingReportsService } from '../accounting-reports.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { ProfitLossPdfGenerator } from './profit-loss-pdf';

@Component({
  selector: 'app-profit-loss',
  standalone: true,
  imports: [FormsModule, CurrencyPipe, AgChartsModule, AgGridAngular, CollapseModule],
  templateUrl: './profit-loss.component.html',
  styleUrl: './profit-loss.component.css',
})
export class ProfitLossComponent implements OnInit {
  public chartOptions!: AgChartOptions;
  public pieChartOptions!: AgChartOptions;
  public period: string = 'This Year';
  public periods: string[] = [
    'This Month',
    'Last Month',
    'This Quarter',
    'This Year',
    'Custom',
  ];
  public isChartCollapsed: boolean = false;
  public isIncomeCollapsed: boolean = true;
  public isExpenseCollapsed: boolean = true;
  public startDate: string = '';
  public endDate: string = '';
  public chartData: any[] = [];
  public expenseBreakdown: any[] = [];

  // AG-Grid configurations
  public theme = agGridDarkTheme;
  public incomeRowData: any[] = [];
  public expenseRowData: any[] = [];
  public gridOptions: GridOptions = {
    theme: agGridDarkTheme,
    defaultColDef: {
      flex: 1,
      minWidth: 100,
      filter: true,
      sortable: true,
      floatingFilter: true,
      resizable: true,
    },
    pagination: true,
    // paginationPageSize is set in the constructor — `this.sharedService`
    // isn't available in this field initializer.
    paginationPageSizeSelector: [10, 20, 50],
    domLayout: 'autoHeight',
  };

  public incomeColDefs: ColDef[] = [
    {
      field: 'date',
      headerName: 'Date',
      valueFormatter: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : '',
    },
    { field: 'account', headerName: 'Account' },
    { field: 'description', headerName: 'Description', flex: 2 },
    {
      field: 'amount',
      headerName: 'Amount',
      cellClass: 'text-end',
      valueFormatter: (params) =>
        params.value ? '$' + params.value.toFixed(2) : '$0.00',
    },
  ];

  public expenseColDefs: ColDef[] = [
    {
      field: 'date',
      headerName: 'Date',
      valueFormatter: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : '',
    },
    { field: 'account', headerName: 'Account' },
    { field: 'description', headerName: 'Description', flex: 2 },
    {
      field: 'amount',
      headerName: 'Amount',
      cellClass: 'text-end text-danger',
      valueFormatter: (params) =>
        params.value ? '$' + params.value.toFixed(2) : '$0.00',
    },
  ];

  // Summary Metrics
  public totalRevenue: number = 0;
  public totalCogs: number = 0;
  public grossProfit: number = 0;
  public totalExpenses: number = 0;
  public netIncome: number = 0;

  public incomeAccounts: any[] = [];
  public expenseAccounts: any[] = [];

  constructor(
    private reportsService: AccountingReportsService,
    public sharedService: SharedService
  ) {
    this.gridOptions.paginationPageSize =
      this.sharedService.default_pagination_page_size;
  }

  ngOnInit() {
    if (this.sharedService.company_info_loaded) {
      this.setPeriod('This Year');
      return;
    }

    this.sharedService.company_info_retrieved$.pipe(take(1)).subscribe(() => {
      this.setPeriod('This Year');
    });
  }

  exportPdf() {
    const generator = new ProfitLossPdfGenerator();
    const companyInfo = this.sharedService.company_info;
    const data = {
      totalRevenue: this.totalRevenue,
      totalCogs: this.totalCogs,
      grossProfit: this.grossProfit,
      totalExpenses: this.totalExpenses,
      netIncome: this.netIncome,
      incomeAccounts: this.incomeAccounts,
      expenseAccounts: this.expenseAccounts
    };
    const doc = generator.generatePdf(data, companyInfo, this.period, this.startDate, this.endDate);
    doc.save(`Profit_Loss_Report_${this.period.replace(' ', '_')}.pdf`);
  }

  loadData() {
    this.reportsService.getProfitLoss(this.startDate, this.endDate).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const d = res.data;
          this.totalRevenue = d.totalRevenue || 0;
          this.totalCogs = d.totalCogs || 0;
          this.grossProfit = d.grossProfit || 0;
          this.totalExpenses = d.totalExpenses || 0;
          this.netIncome = d.netIncome || 0;
          this.incomeAccounts = d.incomeAccounts || [];
          this.expenseAccounts = d.expenseAccounts || [];
          this.chartData = d.chartData || [];
          this.expenseBreakdown = d.expenseBreakdown || [];

          this.incomeRowData = this.flattenTransactions(this.incomeAccounts);
          this.expenseRowData = this.flattenTransactions(this.expenseAccounts);

          this.initCharts();
        }
      },
      error: (err) => console.error('Error loading P&L data:', err),
    });
  }

  private flattenTransactions(accounts: any[]) {
    const flattened: any[] = [];
    accounts.forEach((acc) => {
      if (acc.transactions) {
        acc.transactions.forEach((tx: any) => {
          flattened.push({
            ...tx,
            account: acc.name,
          });
        });
      }
    });
    return flattened;
  }

  setPeriod(p: string) {
    this.period = p;
    if (p === 'Custom') return;

    const today = new Date();
    let start = new Date();
    let end = new Date();

    if (p === 'This Month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (p === 'Last Month') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (p === 'This Quarter') {
      const quarter = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today.getFullYear(), (quarter + 1) * 3, 0);
    } else if (p === 'This Year') {
      start = new Date(today.getFullYear(), 0, 1);
      end = new Date(today.getFullYear(), 11, 31);
    }

    this.startDate = this.formatDate(start);
    this.endDate = this.formatDate(end);
    this.loadData();
  }

  applyCustomRange() {
    if (this.startDate && this.endDate) {
      this.loadData();
    }
  }

  private formatDate(date: Date): string {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
  }

  private initCharts() {
    this.initTrendChart();
    this.initPieChart();
  }

  private initTrendChart() {
    // If we have no chartData yet, provide a single zeroed out month as placeholder
    const data =
      this.chartData && this.chartData.length > 0
        ? this.chartData
        : [{ month: 'Current', revenue: 0, expenses: 0, net: 0 }];

    this.chartOptions = {
      theme: 'ag-default-dark',
      background: { fill: 'transparent' },
      padding: { top: 20, right: 0, bottom: 20, left: 10 },
      data: data,
      series: [
        {
          type: 'bar',
          xKey: 'month',
          yKey: 'revenue',
          yName: 'Revenue',
          fill: '#00a8ff',
          cornerRadius: 4,
        },
        {
          type: 'bar',
          xKey: 'month',
          yKey: 'expenses',
          yName: 'Expenses',
          fill: '#ff4d4d',
          cornerRadius: 4,
        },
        {
          type: 'line',
          xKey: 'month',
          yKey: 'net',
          yName: 'Net Income',
          stroke: '#4caf50',
          strokeWidth: 3,
          marker: { fill: '#4caf50', stroke: '#1e1e1e', strokeWidth: 2 },
        },
      ],
      axes: [
        {
          type: 'category',
          position: 'bottom',
          label: { color: 'rgba(255, 255, 255, 0.7)' },
        },
        {
          type: 'number',
          position: 'left',
          label: {
            color: 'rgba(255, 255, 255, 0.7)',
            formatter: (params: any) => {
              return '$' + params.value.toLocaleString();
            },
          },
          gridLine: { style: [{ stroke: 'rgba(255, 255, 255, 0.1)' }] },
        },
      ],
      legend: {
        item: { label: { color: 'rgba(255, 255, 255, 0.8)' } },
      },
    } as any;
  }

  private initPieChart() {
    const data =
      this.expenseBreakdown && this.expenseBreakdown.length > 0
        ? this.expenseBreakdown
        : [{ name: 'No Expenses', amount: 1 }];

    this.pieChartOptions = {
      theme: 'ag-default-dark',
      background: { fill: 'transparent' },
      padding: { top: 10, right: 10, bottom: 10, left: 10 },
      title: {
        text: 'Expense Distribution',
        color: 'white',
        fontSize: 14,
        spacing: 10,
      },
      data: data,
      series: [
        {
          type: 'donut',
          angleKey: 'amount',
          calloutLabelKey: 'name',
          sectorLabelKey: 'amount',
          innerRadiusRatio: 0.7,
          strokeWidth: 2,
          strokes: ['#1e1e1e'],
          sectorLabel: {
            color: 'white',
            fontWeight: 500,
            formatter: (params: any) => {
              return '$' + (params.value / 1000).toFixed(1) + 'k';
            },
          },
          calloutLabel: {
            enabled: false, // Disable callouts as they cause overflow on narrow screens
          },
          fills: [
            '#ff4d4d',
            '#ff9800',
            '#9c27b0',
            '#e91e63',
            '#673ab7',
            '#3f51b5',
          ],
        },
      ],
      legend: {
        position: 'bottom',
        item: {
          label: { color: 'rgba(255, 255, 255, 0.8)', fontSize: 10 },
          marker: { size: 10 },
        },
      },
    } as any;
  }
}
